import type { GroupLifeDuplicateCpfsPayload } from '@/services/group-life';
import { Button, Dialog, Text } from '@noorden-insurtech/alba-design-system';
import { AlertTriangle } from 'lucide-react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  payload: GroupLifeDuplicateCpfsPayload | null;
};

/**
 * Modal exibido quando o upload de planilha falha com CPF(s) duplicado(s).
 * Substitui o `alert()` cru — agrupa as duplicatas em duas seções com CPF
 * formatado e ponteiros para as linhas da planilha. Mensagem direta e
 * acionável: o corretor sabe O QUE corrigir e ONDE.
 */
export function DuplicateMembersDialog({ isOpen, onClose, payload }: Props) {
  if (!payload) return null;
  const { alreadyInPolicy, repeatedInBatch, totalCount } = payload;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[640px] max-w-[90vw] rounded-[24px] bg-[#F9F9F9] p-0 shadow-xl'>
          <div className='flex flex-col gap-4 p-6'>
            <Dialog.Header className='w-full p-0'>
              <Dialog.Title>
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex items-start gap-3'>
                    <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-alba-yellow-100'>
                      <AlertTriangle className='h-5 w-5 text-alba-yellow-600' />
                    </div>
                    <div className='flex flex-col gap-1'>
                      <Text className='text-lg font-bold text-alba-black-500'>
                        Planilha não importada
                      </Text>
                      <Text className='text-sm text-alba-gray-600'>
                        Encontramos {totalCount} CPF
                        {totalCount > 1 ? 's' : ''} em conflito. Corrija os
                        apontamentos abaixo na planilha e tente novamente.
                      </Text>
                    </div>
                  </div>
                  <Dialog.CloseButton onClick={onClose} />
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body className='w-full p-0'>
              <div className='flex max-h-[420px] flex-col gap-3 overflow-y-auto pr-1'>
                {alreadyInPolicy.length > 0 && (
                  <section className='rounded-lg border border-alba-gray-200 bg-white p-4'>
                    <Text className='mb-2 text-sm font-semibold text-alba-black-700'>
                      Já cadastrad{alreadyInPolicy.length > 1 ? 'os' : 'o'} nesta
                      cotação ({alreadyInPolicy.length})
                    </Text>
                    <ul className='flex flex-col gap-1.5'>
                      {alreadyInPolicy.map(d => (
                        <li
                          key={`already-${d.rawCpf}-${d.rowNumber}`}
                          className='flex items-baseline justify-between gap-3 text-sm'
                        >
                          <span className='font-mono text-alba-black-800'>
                            {d.cpf}
                          </span>
                          <span className='text-alba-gray-600'>
                            linha {d.rowNumber}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {repeatedInBatch.length > 0 && (
                  <section className='rounded-lg border border-alba-gray-200 bg-white p-4'>
                    <Text className='mb-2 text-sm font-semibold text-alba-black-700'>
                      Repetid{repeatedInBatch.length > 1 ? 'os' : 'o'} na própria
                      planilha ({repeatedInBatch.length})
                    </Text>
                    <ul className='flex flex-col gap-1.5'>
                      {repeatedInBatch.map(d => (
                        <li
                          key={`batch-${d.rawCpf}`}
                          className='flex items-baseline justify-between gap-3 text-sm'
                        >
                          <span className='font-mono text-alba-black-800'>
                            {d.cpf}
                          </span>
                          <span className='text-alba-gray-600'>
                            linhas {d.rowNumbers.join(', ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </Dialog.Body>

            <Dialog.Footer className='w-full p-0'>
              <div className='flex w-full items-center justify-end gap-3'>
                <Button variant='primary' size='large' onClick={onClose}>
                  Entendi, vou corrigir
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
